import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    inject,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngxs/store';

import { AuthState, Logout } from '@data/state';
import { ApplicationRoleType } from '@data/enum';
import { enumToDescription } from '@shared/util';

@Component({
    selector: 'app-header-nav',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '(document:click)': 'onDocumentClick($event)' },
    template: `
        <header class="app-header">
            <div class="header-brand">
                <span class="header-brand-name">Sourcing Souk</span>
            </div>
            <div class="d-flex align-items-center gap-2">
                <button class="header-icon-btn" title="Notifications">
                    <span class="mdi mdi-bell-outline"></span>
                </button>
                <div
                    class="header-profile"
                    [class.is-open]="profileOpen()"
                    [class.header-profile--cobalt]="isDistributorSupplier()"
                    (click)="toggleProfile()"
                >
                    <div class="header-avatar">
                        <span>{{ initials() }}</span>
                    </div>
                    <span class="header-username">Hi, {{ fullName() }}</span>
                    <span class="mdi mdi-chevron-down header-caret"></span>

                    @if (profileOpen()) {
                        <div class="profile-dropdown" (click)="$event.stopPropagation()">
                            <div class="profile-card-header">
                                <div class="d-flex align-items-start gap-3">
                                    <div class="profile-avatar">
                                        <span>{{ initials() }}</span>
                                    </div>
                                    <div class="profile-info">
                                        <div class="profile-name">{{ fullName() }}</div>
                                        <div class="profile-role-badge">{{ roleLabel() }}</div>
                                        @if (email()) {
                                            <div class="profile-email">{{ email() }}</div>
                                        }
                                    </div>
                                </div>
                            </div>
                            <div class="profile-divider"></div>
                            <div class="profile-menu">
                                <button
                                    class="profile-menu-item profile-menu-item--logout"
                                    (click)="logout()"
                                >
                                    <span class="mdi mdi-logout"></span>
                                    Sign out
                                </button>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </header>
    `,
})
export class HeaderNavComponent {
    private readonly _store = inject(Store);
    private readonly _el = inject(ElementRef);

    readonly firstName = this._store.selectSignal(AuthState.firstName);
    readonly lastName = this._store.selectSignal(AuthState.lastName);
    readonly fullName = this._store.selectSignal(AuthState.fullName);
    readonly email = this._store.selectSignal(AuthState.email);
    readonly roleType = this._store.selectSignal(AuthState.roleType);
    readonly role = this._store.selectSignal(AuthState.role);

    readonly profileOpen = signal(false);

    readonly isDistributorSupplier = computed(
        () => this.roleType() === ApplicationRoleType.DISTRIBUTOR_SUPPLIER,
    );

    readonly initials = computed(() => {
        const first = this.firstName();
        const last = this.lastName();
        if (first && last) return (first[0] + last[0]).toUpperCase();
        if (first) return first.slice(0, 2).toUpperCase();
        return '?';
    });

    readonly roleLabel = computed(() => {
        const rt = this.roleType();
        return rt !== null
            ? enumToDescription({ enumeration: ApplicationRoleType, enumValue: rt, useExplicitMappings: true })
            : (this.role() ?? '');
    });

    toggleProfile(): void {
        this.profileOpen.update((v) => !v);
    }

    onDocumentClick(event: Event): void {
        if (!this._el.nativeElement.contains(event.target)) {
            this.profileOpen.set(false);
        }
    }

    logout(): void {
        this.profileOpen.set(false);
        this._store.dispatch(new Logout());
    }
}
